import i18next from "i18next";
import I18nextBrowserLanguageDetector from "i18next-browser-languagedetector";
import Fetch from "i18next-fetch-backend";
import { StrictMode, startTransition } from "react";
import { hydrateRoot } from "react-dom/client";
import { I18nextProvider, initReactI18next } from "react-i18next";
import { HydratedRouter } from "react-router/dom";
import { getInitialNamespaces } from "remix-i18next/client";
import { tracesSampler } from "../sentry-sampling.js";
import {
	defaultNS,
	fallbackLng,
	supportedLngs,
} from "./tooling/i18n/i18n.config";
import { reportHydrationError } from "./utils/hydration-error-reporter";
import {
	handleNetworkError,
	isNetworkError,
	setupNetworkErrorHandler,
} from "./utils/network-error-reporter";
import {
	handleVersionMismatch,
	isVersionMismatchError,
	setupVersionMismatchHandler,
} from "./utils/version-mismatch-handler";

declare global {
	interface Window {
		ENV?: {
			SENTRY_DSN?: string;
			ENVIRONMENT?: string;
			SENTRY_RELEASE?: string;
			SENTRY_REPLAY_ENABLED?: boolean;
			SENTRY_USER_ID?: string;
			MAP_TILE_STYLE_URL?: string;
		};
	}
}

async function initSentry() {
	const [Sentry, { beforeSend, deniedUrls, ignoredErrors }] = await Promise.all(
		[import("@sentry/react-router"), import("../sentry-filters.js")],
	);

	if (Sentry.getClient()) {
		return;
	}

	const isReplayEnabled = window.ENV?.SENTRY_REPLAY_ENABLED ?? false;

	Sentry.init({
		dsn: window.ENV?.SENTRY_DSN,
		environment: window.ENV?.ENVIRONMENT ?? "development",
		release: window.ENV?.SENTRY_RELEASE,
		tracesSampler,
		profilesSampleRate: 0.01,
		replaysSessionSampleRate: isReplayEnabled ? 0.01 : 0,
		replaysOnErrorSampleRate: isReplayEnabled ? 1.0 : 0,
		integrations: [
			Sentry.reactRouterTracingIntegration(),
			Sentry.browserProfilingIntegration(),
			...(isReplayEnabled
				? [
						Sentry.replayIntegration({
							maskAllText: false,
							blockAllMedia: false,
						}),
					]
				: []),
			Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
		],
		enableLogs: true,
		ignoreErrors: ignoredErrors,
		denyUrls: deniedUrls,
		beforeSend,
	});

	// Set user context early so hydration errors are associated with users
	const sentryUserId = window.ENV?.SENTRY_USER_ID;
	if (sentryUserId) {
		Sentry.setUser({ id: sentryUserId });
	}
}

function clearBrowserExtensionInjectionsBeforeHydration() {
	document
		.querySelectorAll(
			[
				"html > *:not(body, head)",
				'script[src*="extension://"]',
				'link[href*="extension://"]',
			].join(", "),
		)
		.forEach((s) => {
			s.parentNode?.removeChild(s);
		});

	// Remove inline scripts injected by browser extensions (password managers, auto-fill)
	// These scripts don't have a src attribute and are injected into head/body
	// We identify them by checking for extension-specific patterns in their content
	const extensionPatterns = [
		/xbrowser/i,
		/swbrowser/i,
		/execute_auto_fill/i,
		/password.*manager/i,
		/autofill/i,
	];

	document.querySelectorAll("script:not([src])").forEach((script) => {
		const content = script.textContent ?? "";
		const isExtensionScript = extensionPatterns.some((pattern) =>
			pattern.test(content),
		);
		if (isExtensionScript) {
			script.parentNode?.removeChild(script);
		}
	});

	const $targets = {
		html: {
			// biome-ignore lint/style/noNonNullAssertion:
			// biome-ignore lint/style/useNamingConvention: @todo
			$elm: document.querySelector("html")!,
			allowedAttributes: ["lang", "dir", "class"],
		},
		head: {
			// biome-ignore lint/style/noNonNullAssertion:
			// biome-ignore lint/style/useNamingConvention: @todo
			$elm: document.querySelector("head")!,
			allowedAttributes: [""],
		},
		body: {
			// biome-ignore lint/style/noNonNullAssertion:
			// biome-ignore lint/style/useNamingConvention: @todo
			$elm: document.querySelector("body")!,
			allowedAttributes: ["class"],
		},
	};

	Object.entries($targets).forEach(([targetName, target]) => {
		target.$elm.getAttributeNames().forEach((attr) => {
			if (!target.allowedAttributes.includes(attr)) {
				target.$elm.removeAttribute(attr);
			}
		});
	});
}

// Cooldown between auto-reloads triggered by vite:preloadError. Short enough
// that a fresh deploy still recovers promptly if the user hits multiple chunk
// failures; long enough that a persistently broken build doesn't infinite-loop.
const PRELOAD_RELOAD_COOLDOWN_MS = 60_000;
const PRELOAD_RELOAD_KEY = "vite-preload-reloaded-at";

/**
 * Attempt to recover from a stale chunk preload by hard-reloading.
 *
 * Returns `true` only when we have actually committed to a reload — the
 * caller uses that signal to decide whether to suppress Vite's default
 * error handling via `preventDefault()`. If we return `false` (cooldown
 * still active, or storage access threw) the default error path must run,
 * otherwise the user ends up silently stranded on a broken chunk with no
 * recovery and no visible failure.
 */
function recoverFromStalePreload(): boolean {
	try {
		const previous = Number(sessionStorage.getItem(PRELOAD_RELOAD_KEY) ?? "0");
		if (
			Number.isFinite(previous) &&
			Date.now() - previous < PRELOAD_RELOAD_COOLDOWN_MS
		) {
			return false;
		}
		sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()));
	} catch {
		// Sandboxed iframe, privacy mode, or storage quota exceeded.
		// Let Vite's default error path surface the failure to the user.
		return false;
	}
	window.location.reload();
	return true;
}

async function main() {
	// Recover from Vite dynamic-import preload failures (stale deploys, CDN
	// hiccups, network blips). Attached BEFORE initSentry() so that a preload
	// failure inside Sentry's own lazy chunk still triggers recovery —
	// otherwise the event fires with no listener and the user is stuck.
	// `preventDefault()` tells Vite to treat the rejection as handled; we then
	// perform a hard reload so the new asset hashes are fetched. The reload
	// throttle uses a timestamp (not a boolean) so it naturally re-arms after
	// a short cooldown instead of disabling recovery for the rest of the tab.
	window.addEventListener("vite:preloadError", (event) => {
		// Only suppress Vite's default error handling when we're committed
		// to a reload — otherwise the user would be silently stuck on a
		// broken lazy-route transition.
		if (recoverFromStalePreload()) {
			event.preventDefault();
		}
	});

	try {
		await initSentry();
	} catch (error) {
		console.error(
			"Sentry initialization failed, continuing without Sentry:",
			error,
		);
	}

	// Set up global handlers for version mismatch errors (turbo-stream, manifest)
	// This catches errors during navigation when client bundle is stale
	setupVersionMismatchHandler();

	// Set up global handlers for network errors during fetch operations
	// This captures context about "Load failed" and similar network errors
	setupNetworkErrorHandler();

	await i18next
		.use(initReactI18next) // Tell i18next to use the react-i18next plugin
		.use(Fetch) // Tell i18next to use the Fetch backend
		.use(I18nextBrowserLanguageDetector) // Setup a client-side language detector
		.init({
			defaultNS,
			fallbackLng,
			supportedLngs,
			ns: getInitialNamespaces(),
			react: { useSuspense: false },
			detection: {
				// Here only enable htmlTag detection, we'll detect the language only
				// server-side with remix-i18next, by using the `<html lang>` attribute
				// we can communicate to the client the language detected server-side
				order: ["htmlTag"],
				// Because we only use htmlTag, there's no reason to cache the language
				// on the browser, so we disable it
				caches: [],
			},
			backend: {
				// We will configure the backend to fetch the translations from the
				// resource route /api/locales and pass the lng and ns as search params
				loadPath: "/api/locales?lng={{lng}}&ns={{ns}}",
			},
			interpolation: {
				escapeValue: false, // Not needed for react as it escapes by default
			},
		});

	startTransition(() => {
		clearBrowserExtensionInjectionsBeforeHydration();

		const root = hydrateRoot(
			document,
			<I18nextProvider i18n={i18next}>
				<StrictMode>
					<HydratedRouter />
				</StrictMode>
			</I18nextProvider>,
			{
				onRecoverableError: (error, errorInfo) => {
					// Check if this is a version mismatch error (stale client bundle)
					// If so, trigger a full page reload instead of trying to recover
					if (error instanceof Error && isVersionMismatchError(error)) {
						handleVersionMismatch(error);
						return;
					}

					// Check if this is a network error (fetch failure during navigation)
					// Report with rich network context for debugging
					if (error instanceof Error && isNetworkError(error)) {
						handleNetworkError(error);
						return;
					}

					reportHydrationError(error, errorInfo);

					root.render(
						<I18nextProvider i18n={i18next}>
							<StrictMode>
								<HydratedRouter />
							</StrictMode>
						</I18nextProvider>,
					);
				},
			},
		);
	});
}

const hydrate = () => main().catch((error) => console.error(error));

if (window.requestIdleCallback) {
	window.requestIdleCallback(hydrate);
} else {
	// Safari doesn't support requestIdleCallback
	// https://caniuse.com/requestidlecallback
	window.setTimeout(hydrate, 1);
}
